"use client"

/**
 * ConfigureViewButton — opens the ConfigureViewDrawer. Drop it next to a list
 * page's Refresh button. Owns the drawer's open state; the drawer stages edits
 * in a draft and hands the committed config back through `onSave`.
 *
 * When the saved config equals the module defaults we drop the localStorage
 * key (via `reset`) instead of persisting a redundant copy, so "no stored
 * config" stays the canonical default state.
 */

import { SettingOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import { useState } from "react"
import { t } from "@/languages/i18n"
import { ConfigureViewDrawer, type ConfigureViewFeatures } from "./ConfigureViewDrawer"
import type { ColumnMeta, ViewConfig } from "./types"
import { viewConfigEqual } from "./use-view-config"

interface ConfigureViewButtonProps {
  registry: ColumnMeta[]
  config: ViewConfig
  defaultConfig: ViewConfig
  setConfig: (next: ViewConfig) => void
  reset: () => void
  features?: ConfigureViewFeatures
  pageSizeOptions?: number[]
  /** Show a text label beside the icon. Off by default (icon-only). */
  showLabel?: boolean
}

export function ConfigureViewButton({
  registry,
  config,
  defaultConfig,
  setConfig,
  reset,
  features,
  pageSizeOptions,
  showLabel,
}: ConfigureViewButtonProps) {
  const [open, setOpen] = useState(false)

  const handleSave = (next: ViewConfig) => {
    if (viewConfigEqual(next, defaultConfig)) reset()
    else setConfig(next)
  }

  return (
    <>
      <Tooltip title={t("Cấu hình hiển thị")}>
        <Button icon={<SettingOutlined />} onClick={() => setOpen(true)} aria-label={t("Cấu hình hiển thị")}>
          {showLabel ? t("Cấu hình hiển thị") : null}
        </Button>
      </Tooltip>
      <ConfigureViewDrawer
        open={open}
        onClose={() => setOpen(false)}
        registry={registry}
        config={config}
        defaultConfig={defaultConfig}
        onSave={handleSave}
        features={features}
        pageSizeOptions={pageSizeOptions}
      />
    </>
  )
}

export default ConfigureViewButton
